import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMarket } from "../../lib/store";
import { useSubscribe } from "../../lib/socket";
import { api } from "../../lib/api";
import { fmt, pct, tone, inr, signed } from "../../lib/format";
import { useMotion } from "./MotionProvider";
import { useTween, Tilt } from "./primitives";

// 02 ticker tape — live LTP + change% of indices and watchlist symbols
export function TickerTape({ keys }) {
  const ticks = useMarket((s) => s.ticks);
  const meta = useMarket((s) => s.meta);
  const nav = useNavigate();
  const items = keys.filter((k) => ticks[k]?.ltp);
  if (!items.length) return null;
  const row = items.map((k) => (
    <button key={k} onClick={() => nav(`/instrument/${encodeURIComponent(k)}`)} className="flex items-center gap-2 px-4 text-[11.5px] hover:text-primary transition-colors duration-150">
      <span className="font-medium">{meta[k]?.trading_symbol || k.split("|")[1]}</span>
      <span className="num">{fmt(ticks[k].ltp)}</span>
      <span className={`num ${tone(ticks[k].chp)}`}>{pct(ticks[k].chp)}</span>
    </button>));
  return (
    <div className="relative overflow-hidden border-y border-border bg-card/60 h-8 flex items-center -mx-4 lg:-mx-6" data-testid="ticker-tape">
      <div className="m-ticker flex whitespace-nowrap" style={{ animationDuration: `${Math.max(30, items.length * 4)}s` }}>{row}<span aria-hidden="true" className="flex">{row}</span></div>
      <span className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-background to-transparent" />
      <span className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent" />
    </div>
  );
}

// 10 risk gauge — margin used % (prop rules are not live yet)
export function RiskGauge({ used, total }) {
  const p = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const v = useTween(p, 900);
  const color = v > 80 ? "hsl(var(--down))" : v >= 60 ? "#F5B84A" : "hsl(var(--gold))";
  const len = Math.PI * 80;
  return (
    <div className="flex flex-col items-center py-3" data-testid="risk-gauge">
      <svg viewBox="0 0 200 112" className="w-48">
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="hsl(var(--border))" strokeWidth="12" strokeLinecap="round" />
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" strokeDasharray={len} strokeDashoffset={len * (1 - v / 100)} style={{ transition: "stroke .4s" }} />
      </svg>
      <div className="-mt-12 text-center">
        <div className="num text-[24px] font-semibold" style={{ color }} data-testid="risk-gauge-value">{v.toFixed(1)}%</div>
        <div className="text-[10.5px] text-muted-foreground">margin used · {inr(used)} of {inr(total)}</div>
      </div>
    </div>
  );
}

// 11 target progress — hidden until the challenge rules engine exists
export const CHALLENGE_MODE = false;
export function TargetProgress({ value, target }) {
  const p = Math.max(0, Math.min(100, (value / target) * 100));
  const v = useTween(p, 900);
  return (
    <div className="px-4 py-4" data-testid="target-progress">
      <div className="relative h-2.5 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full ${v >= 100 ? "bg-up" : "bg-gold"}`} style={{ width: `${v}%` }} />
        {[25, 50, 75].map((t) => <span key={t} className="absolute top-0 bottom-0 w-px bg-background/70" style={{ left: `${t}%` }} />)}
      </div>
      <div className="flex justify-between text-[11px] mt-1.5 num"><span>{signed(value)}</span><span className="text-muted-foreground">target {inr(target)}</span></div>
    </div>
  );
}

// 12 liquid fill — margin used % circle
export function LiquidFill({ used, total }) {
  const p = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const v = useTween(p, 1000);
  const { mode } = useMotion();
  return (
    <div className="flex items-center gap-4 px-4 py-4" data-testid="liquid-fill">
      <div className="relative h-24 w-24 rounded-full border border-primary/40 overflow-hidden bg-background">
        <div className="absolute inset-x-0 bottom-0 transition-[height] duration-700" style={{ height: `${v}%` }}>
          <svg viewBox="0 0 200 20" preserveAspectRatio="none" className={`absolute -top-[9px] left-0 w-[200%] h-3 ${mode === "off" ? "" : "m-wave"}`}><path d="M0 10 Q25 0 50 10 T100 10 T150 10 T200 10 V20 H0 Z" fill="hsl(var(--gold) / 0.55)" /></svg>
          <div className="h-full bg-[hsl(var(--gold)/0.55)]" />
        </div>
        <div className="absolute inset-0 grid place-items-center num text-[16px] font-semibold" data-testid="liquid-fill-value">{v.toFixed(0)}%</div>
      </div>
      <div className="text-[12px]"><div className="eyebrow">Margin used</div><div className="num mt-1">{inr(used)}</div><div className="text-muted-foreground text-[11px]">of {inr(total)} total margin</div></div>
    </div>
  );
}

// 25 sector heatmap — live sector index change %
const SECTORS = ["Nifty Bank", "Nifty IT", "Nifty Auto", "Nifty Pharma", "Nifty FMCG", "Nifty Metal", "Nifty Realty", "Nifty Energy", "Nifty Media", "Nifty PSU Bank", "Nifty Pvt Bank", "Nifty Fin Service"].map((n) => `NSE_INDEX|${n}`);
export function SectorHeatmap() {
  useSubscribe(SECTORS);
  const ticks = useMarket((s) => s.ticks);
  const nav = useNavigate();
  const rows = SECTORS.filter((k) => ticks[k]?.chp != null);
  if (!rows.length) return <div className="px-4 py-6 text-[12px] text-muted-foreground">Waiting for sector prices…</div>;
  return (
    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5 p-3" data-testid="sector-heatmap">
      {rows.map((k) => {
        const c = ticks[k].chp;
        const a = Math.min(0.85, 0.12 + Math.abs(c) / 3);
        return (
          <button key={k} onClick={() => nav(`/instrument/${encodeURIComponent(k)}`)} data-testid={`sector-tile-${k.split("|")[1].replace(/\s/g, "-")}`}
            className="rounded-md px-2.5 py-2 text-left transition-[background-color,transform] duration-500 hover:scale-[1.03]"
            style={{ backgroundColor: c >= 0 ? `hsl(var(--up) / ${a})` : `hsl(var(--down) / ${a})` }}>
            <div className="text-[10.5px] font-medium truncate">{k.split("|")[1].replace("Nifty ", "")}</div>
            <div className="num text-[12.5px] font-semibold">{pct(c)}</div>
          </button>);
      })}
    </div>
  );
}

// 05 equity curve — account equity history from the ledger
export function EquityCurve({ days = 180, height = 180 }) {
  const [pts, setPts] = useState(null);
  const v = useMarket((s) => s.portfolioVersion);
  useEffect(() => { api.get("/reports/equity", { params: { days } }).then((r) => setPts(r.data)).catch(() => setPts([])); }, [days, v]);
  const geo = useMemo(() => {
    if (!pts || pts.length < 2) return null;
    const ys = pts.map((p) => p.equity);
    const lo = Math.min(...ys), hi = Math.max(...ys), span = hi - lo || 1;
    const xy = pts.map((p, i) => [(i / (pts.length - 1)) * 600, 10 + (1 - (p.equity - lo) / span) * (height - 30)]);
    const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    return { line, area: `${line} L600 ${height} L0 ${height} Z`, last: pts[pts.length - 1], first: pts[0] };
  }, [pts, height]);
  if (!pts) return <div className="m-skel mx-4 my-4 rounded" style={{ height }} />;
  if (!geo) return <div className="px-4 py-8 text-[12px] text-muted-foreground" data-testid="equity-curve-empty">Equity history appears after your first trading day.</div>;
  const chg = geo.last.equity - geo.first.equity;
  return (
    <div className="px-2 pb-2" data-testid="equity-curve">
      <div className="flex items-baseline justify-between px-2 pt-2"><span className="num text-[18px] font-semibold">{inr(geo.last.equity)}</span><span className={`num text-[12px] ${tone(chg)}`}>{signed(chg)} since {geo.first.date}</span></div>
      <svg viewBox={`0 0 600 ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
        <defs><linearGradient id="eqg" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="hsl(var(--gold))" stopOpacity="0.32" /><stop offset="1" stopColor="hsl(var(--gold))" stopOpacity="0" /></linearGradient></defs>
        <path d={geo.area} fill="url(#eqg)" className="m-fade" />
        <path key={geo.line} d={geo.line} fill="none" stroke="hsl(var(--gold))" strokeWidth="2" vectorEffect="non-scaling-stroke" className="m-draw" pathLength="1" />
      </svg>
    </div>
  );
}

// 28 flipping stats
function FlipCard({ label, value, testid }) {
  const [face, setFace] = useState(value);
  const [flip, setFlip] = useState(false);
  const { mode } = useMotion();
  useEffect(() => {
    if (value === face) return undefined;
    if (mode === "off") { setFace(value); return undefined; }
    setFlip(true);
    const t = setTimeout(() => { setFace(value); setFlip(false); }, 260);
    return () => clearTimeout(t);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="rounded-lg border border-border bg-background/40 px-3 py-2.5 [perspective:600px]" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="num text-[15px] font-semibold mt-1 transition-transform duration-[260ms]" style={{ transform: flip ? "rotateX(90deg)" : "rotateX(0deg)", transitionTimingFunction: "var(--m-ease)" }}>{face}</div>
    </div>
  );
}
export function FlipStats({ s, daily }) {
  const wins = daily.filter((d) => d.net > 0).reduce((a, d) => a + d.net, 0);
  const losses = daily.filter((d) => d.net < 0).reduce((a, d) => a + d.net, 0);
  let peak = 0, cum = 0, dd = 0;
  daily.forEach((d) => { cum += d.net; peak = Math.max(peak, cum); dd = Math.min(dd, cum - peak); });
  const items = [["Win rate", `${s.win_rate}%`], ["Avg win", inr(s.avg_win)], ["Avg loss", inr(s.avg_loss)], ["Max drawdown", inr(dd)],
    ["Profit factor", losses ? (wins / -losses).toFixed(2) : wins ? "∞" : "—"], ["Trades", String(s.trades)], ["Best day", s.best_day ? signed(s.best_day.net) : "—"]];
  return <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2 p-3" data-testid="flip-stats">{items.map(([l, v]) => <FlipCard key={l} label={l} value={v} testid={`flip-stat-${l.toLowerCase().replace(/\s/g, "-")}`} />)}</div>;
}

// 23 market pulse
const PHASE = { OPEN: ["Open", "bg-up", true], PRE_OPEN: ["Pre-open", "bg-amber-500", true], CLOSED: ["Closed", "bg-muted-foreground", false], HOLIDAY: ["Holiday", "bg-muted-foreground", false] };
export function MarketPulse({ ex = "NSE" }) {
  const st = useMarket((s) => s.status?.exchanges?.[ex]);
  const [label, color, live] = PHASE[st] || [st ? st.replace("_", " ") : "…", "bg-muted-foreground", false];
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={`market-pulse-${ex}`} title={`${ex} market ${label.toLowerCase()}`}>
      <span className="relative flex h-2 w-2">
        {live && <span className={`absolute inline-flex h-full w-full rounded-full ${color} opacity-70 m-ping`} />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${color}`} />
      </span>
      <span>{ex} · {label}</span>
    </span>
  );
}

// 23 ECG line with real round-trip latency over the live market socket
export function ConnectionHealth() {
  const samples = useMarket((s) => s.latency);
  const rate = useMarket((s) => s.tickRate);
  const ws = useMarket((s) => s.wsState);
  const last = samples[samples.length - 1];
  const max = Math.max(200, ...samples);
  const d = samples.map((v, i) => { const x = (i / 39) * 300; const y = 50 - (v / max) * 40; return `${x},44 ${x + 2},${y} ${x + 4},48 ${x + 7.5},44`; }).join(" ");
  return (
    <div className="px-4 py-3" data-testid="connection-health">
      <div className="flex justify-between text-[12px]"><span>Connection health</span>
        <span className="num"><span className={ws === "open" ? "text-up" : "text-down"} data-testid="connection-ws-state">{ws === "open" ? "Feed connected" : "Reconnecting"}</span> · <span data-testid="connection-latency">{last != null ? `${last} ms` : "…"}</span> · <span data-testid="connection-tick-rate">{rate} ticks/s</span></span></div>
      <svg viewBox="0 0 300 56" className="w-full h-14 mt-1"><polyline points={d} fill="none" stroke="hsl(var(--gold))" strokeWidth="1.4" strokeLinejoin="round" /></svg>
      <div className="text-[10.5px] text-muted-foreground">Round-trip time over the live market socket (sampled every 2 s) and price updates received per second</div>
    </div>
  );
}

// 19 3D account card
export function AccountCard({ user, funds }) {
  return (
    <Tilt className="relative rounded-2xl p-5 overflow-hidden border border-primary/30 bg-[linear-gradient(135deg,#17140d,#0b0b0d_55%,#1a1508)] text-[#f4efe4] shadow-gold" data-testid="account-card-3d">
      <span className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(400px circle at var(--gx,30%) var(--gy,20%), hsl(var(--gold)/0.25), transparent 45%)" }} />
      <div className="relative flex justify-between items-start">
        <div><div className="text-[10px] tracking-[0.25em] text-[hsl(43_60%_60%)]">TRADING ACCOUNT</div><div className="font-display text-[15px] font-bold mt-1">{user?.name}</div></div>
        <span className="text-[9px] font-bold tracking-widest border border-[hsl(43_78%_54%/0.5)] text-[hsl(46_95%_72%)] rounded px-1.5 py-0.5">{user?.role === "admin" ? "ADMIN" : "ACTIVE"}</span>
      </div>
      <div className="relative mt-8"><div className="text-[10px] tracking-widest text-[#8c8577]">CASH BALANCE</div><div className="num text-[26px] font-semibold text-gold" data-testid="account-card-balance">{inr(funds?.cash_balance)}</div></div>
      <div className="relative mt-4 flex justify-between text-[11px] font-mono-plex text-[#b9b2a3]"><span data-testid="account-card-id">{user?.login || (user?.id || "").slice(0, 12).toUpperCase()}</span><span>Available {inr(funds?.available_margin)}</span></div>
    </Tilt>
  );
}

// 29 (small) first filled order celebration
export function FirstFillBadge() {
  const [show, setShow] = useState(null);
  const seen = useRef(false);
  useEffect(() => {
    const on = (e) => {
      const uid = e.detail?.user_id;
      const key = `pd_first_fill_${uid}`;
      if (seen.current || !uid || localStorage.getItem(key)) return;
      seen.current = true;
      api.get("/reports/tradebook", { params: { start: new Date(Date.now() - 365 * 864e5).toISOString().slice(0, 10) } }).then((r) => {
        localStorage.setItem(key, "1");
        if (r.data.every((t) => t.order_id === e.detail.id)) { setShow(e.detail); setTimeout(() => setShow(null), 4200); }
      }).catch(() => { seen.current = false; });
    };
    window.addEventListener("pd:fill", on);
    return () => window.removeEventListener("pd:fill", on);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed z-[60] top-16 left-1/2 -translate-x-1/2 m-pop rounded-full border border-primary/50 glass shadow-gold px-5 py-2.5 flex items-center gap-3" data-testid="first-fill-badge" role="status">
      <span className="grid place-items-center h-7 w-7 rounded-full bg-gold text-[#0b0b0d] font-bold text-[13px]">1</span>
      <span className="text-[12.5px]"><b>First trade filled!</b> <span className="text-muted-foreground">{show.side} {show.quantity} {show.symbol} @ ₹{show.avg_price}</span></span>
    </div>
  );
}
